'use client'

import { motion, useScroll, useTransform } from 'framer-motion'
import { useRef } from 'react'
import { RouteLink } from '@/lib/router'
import {
  allClientProjects,
  allLabProjects,
  featuredProjects,
} from '@/lib/portfolioDisplay'
import { PortfolioCard } from './portfolio-card'
import { PortfolioGridCard } from './portfolio-grid-card'
import { RevealText } from './reveal-text'

type WorkSectionProps = {
  mode?: 'featured' | 'full'
}

export function WorkSection({ mode = 'featured' }: WorkSectionProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  })
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '20%'])

  const isFull = mode === 'full'
  const clientList = isFull ? allClientProjects : featuredProjects
  const labList = isFull ? allLabProjects : []
  const projectGrid = (projects: typeof allClientProjects, indexOffset = 0) => (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {projects.map((study, index) => (
        <PortfolioGridCard key={study.id} study={study} index={indexOffset + index} />
      ))}
    </div>
  )

  return (
    <section
      ref={sectionRef}
      id="work"
      className={
        isFull
          ? 'relative z-10 overflow-hidden bg-background px-6 py-20 md:py-24 lg:px-12'
          : 'relative z-10 overflow-hidden bg-background px-6 py-32 md:py-48 lg:px-12'
      }
    >
      <motion.div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{ y: backgroundY }}
      >
        <motion.div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'repeating-linear-gradient(0deg, transparent, transparent 100px, currentColor 100px, currentColor 101px)',
          }}
        />
      </motion.div>

      <div className="relative mx-auto max-w-7xl">
        {isFull ? (
          <div className="mb-10 max-w-3xl md:mb-12">
            <p className="mb-3 text-xs uppercase tracking-[0.3em] text-accent">Work</p>
            <h1 className="font-serif text-3xl tracking-tight md:text-5xl">Sites &amp; products</h1>
            <p className="mt-4 text-base text-muted-foreground md:text-lg">
              Client launches and lab builds from our Vancouver studio - browse the full library
              below.
            </p>
          </div>
        ) : (
          <div className="mb-24 grid gap-8 md:mb-32 md:grid-cols-12">
            <div className="md:col-span-8">
              <RevealText className="mb-4">
                <span className="text-xs tracking-[0.3em] uppercase text-accent">Selected Work</span>
              </RevealText>
              <RevealText delay={0.1}>
                <h2 className="font-serif text-4xl tracking-tight md:text-6xl lg:text-7xl">
                  Sites and software
                  <br />
                  <span className="italic text-muted-foreground">that ship.</span>
                </h2>
              </RevealText>
            </div>
            <div className="md:col-span-4 md:flex md:items-end">
              <RevealText delay={0.2}>
                <p className="font-serif text-lg italic text-muted-foreground">
                  Live client launches and lab builds - marketing sites, POS, booking, CRM, and
                  internal tools in production.
                </p>
              </RevealText>
            </div>
          </div>
        )}

        {isFull ? (
          <>
            <p className="mb-8 text-xs uppercase tracking-[0.25em] text-accent">Live in production</p>
            {projectGrid(clientList)}

            {labList.length > 0 && (
              <>
                <p className="mb-8 mt-16 text-xs uppercase tracking-[0.25em] text-accent md:mt-20">
                  Prototypes &amp; experiments
                </p>
                {projectGrid(labList, clientList.length)}
              </>
            )}
          </>
        ) : (
          <>
            <div className="space-y-24 md:space-y-32">
              {clientList.map((study, index) => (
                <PortfolioCard key={study.id} study={study} index={index} />
              ))}
            </div>

            {labList.length > 0 && (
              <>
                <p className="mb-12 mt-32 text-xs uppercase tracking-[0.25em] text-accent">
                  Prototypes & experiments
                </p>
                <div className="space-y-24 md:space-y-32">
                  {labList.map((study, index) => (
                    <PortfolioCard key={study.id} study={study} index={index} />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {!isFull && (
          <motion.div
            className="mt-24 border-t border-border pt-16 text-center md:mt-32"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <RouteLink
              to="/portfolio"
              className="inline-flex items-center gap-4 py-2.5 font-serif text-2xl transition-colors hover:text-accent md:text-3xl"
            >
              View all work
              <motion.span className="inline-block" whileHover={{ x: 8 }} transition={{ duration: 0.3 }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </motion.span>
            </RouteLink>
          </motion.div>
        )}
      </div>
    </section>
  )
}
